import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { api, ApiError } from '../api/client';
import { endpoints, websocketUrl } from '../api/endpoints';
import { ErrorBox } from '../components/common';
import { LiveSessionClient, type LiveMessage } from '../components/live/LiveSessionClient';
import { browserSpeaking, speakWithBrowser, stopBrowserSpeech } from '../components/live/webSpeech';

interface Turn {
  who: 'you' | 'assistant' | 'action';
  text: string;
  audio?: string | null;
}

export function LiveSessionPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<'idle' | 'connecting' | 'connected' | 'closed'>('idle');
  const [stage, setStage] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState('');
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [finished, setFinished] = useState(false);
  const client = useRef<LiveSessionClient | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const player = useRef<HTMLAudioElement | null>(null);
  const language = useRef<string | null>(null);

  useEffect(() => {
    client.current = new LiveSessionClient({
      onOpen: () => setState('connected'),
      onClose: () => setState('closed'),
      onMessage: (m: LiveMessage) => {
        switch (m.type) {
          case 'session':
            language.current = (m.language as string | undefined) ?? null;
            break;
          case 'status':
            setStage(String(m.stage));
            break;
          case 'transcript':
            setTurns((x) => [...x, { who: 'you', text: String(m.text) }]);
            break;
          case 'response': {
            const audio = (m.audio_url as string | null | undefined) ?? null;
            setTurns((x) => [...x, { who: 'assistant', text: String(m.text ?? ''), audio }]);
            if (audio && player.current) {
              player.current.src = audio;
              void player.current.play().catch(() => undefined);
            } else if (!audio && !m.interrupted && m.text) {
              speakWithBrowser(String(m.text), language.current);
            }
            break;
          }
          case 'action':
            setTurns((x) => [...x, { who: 'action', text: t('live.action', { command: JSON.stringify(m.command) }) }]);
            break;
          case 'error':
            setError(new ApiError(0, String(m.code ?? 'live_error'), String(m.message ?? '')));
            break;
          case 'session_finished':
            setFinished(true);
            break;
          default:
            break;
        }
      },
    });
    return () => {
      client.current?.close();
      recorder.current?.stream.getTracks().forEach((tr) => tr.stop());
      stopBrowserSpeech();
    };
  }, [t]);

  useEffect(() => {
    if (!id || !client.current) return;
    const fromApi = (location.state as { websocketUrl?: string } | null)?.websocketUrl;
    setState('connecting');
    client.current.connect(websocketUrl(id, fromApi, api.tokens.getAccessToken()));
  }, [id, location.state]);

  const startNew = async () => {
    setError(null);
    try {
      const created = await endpoints.sessions.create(null, null);
      navigate(`/live/${created.session_id}`, { state: { websocketUrl: created.websocket_url } });
    } catch (err) {
      setError(err);
    }
  };

  const toggleRecording = async () => {
    setError(null);
    if (recording) {
      recorder.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
        const format = (rec.mimeType || 'audio/webm').split('/')[1]?.split(';')[0] || 'webm';
        void client.current?.sendAudio(blob, format).catch(setError);
      };
      recorder.current = rec;
      rec.start();
      if ((player.current && !player.current.paused) || browserSpeaking()) {
        player.current?.pause();
        stopBrowserSpeech();
        client.current?.bargeIn();
      }
      setRecording(true);
      setStage('listening');
    } catch {
      setError(new Error(t('live.micDenied')));
    }
  };

  const sendText = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      client.current?.sendText(text.trim());
      setTurns((x) => [...x, { who: 'you', text: text.trim() }]);
      setText('');
    } catch (err) {
      setError(err);
    }
  };

  if (!id) {
    return (
      <div>
        <h1>{t('live.title')}</h1>
        <ErrorBox error={error} />
        <button type="button" onClick={() => void startNew()}>
          {t('sessions.new')}
        </button>
      </div>
    );
  }

  const connected = state === 'connected';
  return (
    <div>
      <h1>{t('live.title')}</h1>
      <p role="status" aria-live="polite" className="muted">
        {t(`live.${state === 'idle' ? 'disconnected' : state === 'closed' ? 'disconnected' : state}`)}
        {stage && ` · ${t(`live.stage.${stage}`, { defaultValue: stage })}`}
      </p>
      <ErrorBox error={error} />
      {finished && (
        <p role="status" className="alert alert-ok">
          {t('live.finished')} <Link to={`/sessions/${id}`}>{t('sessions.details')}</Link>
        </p>
      )}
      <section className="card transcript" aria-label={t('live.transcript')} aria-live="polite">
        {turns.map((turn, i) => (
          <div key={i} className={`turn turn-${turn.who}`}>
            <strong>{turn.who === 'you' ? t('live.you') : turn.who === 'assistant' ? t('live.assistant') : '⚙'}:</strong> {turn.text}
          </div>
        ))}
      </section>
      <audio ref={player} aria-label={t('live.assistant')} controls />
      <div className="actions">
        <button type="button" disabled={!connected} aria-pressed={recording} onClick={() => void toggleRecording()}>
          {recording ? t('live.stop') : t('live.record')}
        </button>
        <button
          type="button"
          className="secondary"
          disabled={!connected}
          onClick={() => {
            player.current?.pause();
            stopBrowserSpeech();
            client.current?.bargeIn();
          }}
        >
          {t('live.bargeIn')}
        </button>
        <button
          type="button"
          className="danger"
          disabled={!connected}
          onClick={async () => {
            try {
              client.current?.finish();
              await endpoints.sessions.finish(id).catch(() => undefined);
              setFinished(true);
            } catch (err) {
              setError(err);
            }
          }}
        >
          {t('live.finish')}
        </button>
      </div>
      <form className="inline-form" onSubmit={sendText}>
        <label className="sr-only" htmlFor="live-text">
          {t('live.textPlaceholder')}
        </label>
        <input id="live-text" placeholder={t('live.textPlaceholder')} value={text} onChange={(e) => setText(e.target.value)} disabled={!connected} />
        <button type="submit" disabled={!connected}>
          {t('live.send')}
        </button>
      </form>
    </div>
  );
}
